import React from "react";
import { ArrowLeft, User, Check, LogIn, UserCheck, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBookingDraft } from "../pages/booking/BookingDraftContext";
import { getStaffRegistry } from "@/lib/staff/staffRegistrySingleton";
import { isClientLoggedIn } from "../pages/client/ClientAuthContext";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { getApiBaseUrl } from "../publicSessionService";
import { useTranslation } from "react-i18next";
import { WidgetLanguageSelector } from "./WidgetLanguageSelector";
import { TOP_PILL } from "../look/topPill";

interface WidgetHeaderProps {
  onBack: () => void;
  onGoToSpecialist: () => void;
  onGoToServices: () => void;
  title?: string;
  onOpenClientDashboard?: () => void;
  onOpenSupport?: () => void;
}

export function WidgetHeader({
  onBack,
  onGoToSpecialist,
  onGoToServices,
  title = getCompanyRegistry().getSettings()?.public_title ?? "",
  onOpenClientDashboard,
  onOpenSupport,
}: WidgetHeaderProps) {
  const { t } = useTranslation();
  const { draft } = useBookingDraft();

  const specialistName = React.useMemo(() => {
    if (!draft.specialistId) return null;
    if (draft.specialistId === "any") return t("widget.specialists.any", "Any");
    const staff = getStaffRegistry().getStaffByIdMap()[Number(draft.specialistId)];
    return staff?.name ?? t("widget.common.specialist", "Specialist");
  }, [draft.specialistId, t]);

  const serviceCount = (draft.serviceIds ?? []).length;

  return (
    <div className="p-4 border-b border-border/10">
      {/* Top row: Support + language (left) + Client Dashboard (right) */}
      {
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            {onOpenSupport ? (
              <button
                className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors cursor-pointer ${TOP_PILL}`}
                onClick={onOpenSupport}
                title={t("widget.support.need_help", "Need help?")}
              >
                <HelpCircle className="w-5 h-5" />
                <span className="text-sm font-medium">{t("widget.support.label", "Help")}</span>
              </button>
            ) : null}
            <WidgetLanguageSelector />
          </div>
          {onOpenClientDashboard && getCompanyRegistry().getSettings()?.client_account_enabled === true && (
            <button
              className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors cursor-pointer ${TOP_PILL}`}
              onClick={onOpenClientDashboard}
              title={t("widget.client.dashboard", "My account")}
            >
              {isClientLoggedIn() ? (
                <UserCheck className="w-5 h-5" />
              ) : (
                <LogIn className="w-5 h-5" />
              )}
              <span className="text-sm font-medium">{t("widget.client.dashboard", "My account")}</span>
            </button>
          )}
        </div>
      }

      {/* Bottom row: Back button + Logo + Title + selection pills */}
      <div className="flex items-center gap-3">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          className="p-2 h-auto rounded-full hover:bg-muted/50 shrink-0"
          onClick={onBack}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div className="flex items-center gap-3 flex-1 overflow-hidden">
          <div className="w-10 h-10 shrink-0 rounded-full overflow-hidden">
            {getCompanyRegistry().getSettings()?.logo ? (
              <img
                src={`${getApiBaseUrl()}${getCompanyRegistry().getSettings()?.logo}`}
                alt={getCompanyRegistry().getSettings()?.public_title ?? t("widget.common.logo", "Logo")}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full rounded-full bg-gradient-to-br from-emerald-400 via-blue-500 to-purple-600 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          <div className="flex flex-col justify-center min-w-0">
            <span className="font-semibold text-lg leading-tight truncate">{title}</span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1 shrink-0">
          {specialistName && (
            <div
              onClick={onGoToSpecialist}
              className="group cursor-pointer flex items-center gap-1.5 bg-muted/30 hover:bg-muted/60 border border-border/50 rounded-full pl-1 pr-2.5 py-0.5 transition-colors"
            >
              <div className="w-5 h-5 rounded-full bg-background flex items-center justify-center shadow-sm">
                <User className="w-3 h-3 text-primary" />
              </div>
              <span className="text-xs font-medium max-w-[80px] truncate">{specialistName}</span>
            </div>
          )}

          {serviceCount > 0 && (
            <div
              onClick={onGoToServices}
              className="group cursor-pointer flex items-center gap-1.5 bg-muted/30 hover:bg-muted/60 border border-border/50 rounded-full pl-1 pr-2.5 py-0.5 transition-colors"
            >
              <div className="w-5 h-5 rounded-full bg-background flex items-center justify-center shadow-sm">
                <Check className="w-3 h-3 text-primary" />
              </div>
              <span className="text-xs font-medium">
                {t("widget.common.service_count_short", "{{count}} svc.", { count: serviceCount })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
